import React, { useEffect, useState, useRef } from 'react';
import { useVirtualBackgroundContext, useInitializeCard } from 'contexts/VirtualBackgroundContext';
import { useParams, useNavigate } from 'react-router-dom';
import { getCardById } from 'apis/cardApi'; // Adjust the import path as needed
import { fetchVirtualBackgroundImages } from 'apis/virtualBackgroundApi'; // Ensure this import path is correct
import { NorthIcon, ColorizeOutlinedIcon } from 'icons/materialIcons';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Checkbox,
    Grid,
    FormControlLabel,
    FormControl,
    Button
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { set } from 'react-hook-form';
import TeamVirtualBackgroundCustomLogo from './TeamVirtualBackgroundCustomLogo';
import { useTeam } from 'team/contexts/teamContext';
import { useTeamAssets } from 'team/hooks/useTeamAssets';
import { TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import TeamAssetPickerPopover from 'team/components/editMember/about/profilePhotoCoverPhotoCompanyLogo/TeamAssetPickerPopover';

import TeamUploadImage from './TeamUploadImage';

const TeamVirtualBackgroundContainer = ({
    contextId,
    enableCardFetch = true,
    externalPage,
    onExternalPageChange,
    onPaginationInfo,
    hidePagination = false,
    hideHeading = false,
    showTeamAssetsTab = true,
    teamAssets = [],
    qrColorRowWidth = '60%'
}) => {
    const [selectedButton, setSelectedButton] = useState('library'); // 'library' button selected by default

    const [refreshKey, setRefreshKey] = useState(0);

    const params = useParams();
    const cardId = contextId || params.cardId;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [cardDetails, setCardDetails] = useState({});
    const colorOptions = ['#000000', '#eb5757', '#f2994b', '#f2c84b', '#219654', '#2f82ed'];
    const [showQRCodeColorPicker, setShowQRCodeColorPicker] = useState(false);
    const cardPickerRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [imagesPerPage] = useState(12); // 3 columns x 4 rows






    const [qrCodeColor, setQrCodeColor] = useState('#000000'); // Local state for QR code color
    const [images, setImages] = useState([]); // State to hold virtual background images

    // In VirtualBackgroundContainer.js, add these with your other state declarations
    const [uploadedImage, setUploadedImage] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [assetPickerAnchorEl, setAssetPickerAnchorEl] = useState(null);
    const [pendingAsset, setPendingAsset] = useState(null);
    const { currentTeamId } = useTeam();
    const { assets: fetchedTeamAssets = [] } = useTeamAssets(currentTeamId, TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND);
    const availableTeamAssets = teamAssets.length ? teamAssets : fetchedTeamAssets;



    useInitializeCard(cardId); // Ensure initialization happens before accessing values

    useEffect(() => {
        if (!enableCardFetch || !cardId) {
            setLoading(false);
        }
    }, [enableCardFetch, cardId]);



    const handleCancel = () => {
        if (cardId) {
            setVirtualBackgroundContextQRCodeColor(cardId, '#000000');
            setVirtualBackgroundContextQRCodeLogoUrl(cardId, null); // Add this line
            // Reset other settings as needed
            setRefreshKey(prev => prev + 1);
        }
    };


    // Fetch card details
    useEffect(() => {
        if (!enableCardFetch || !cardId) {
            return;
        }
        const fetchCardDetails = async () => {
            try {
                console.log("card id from virtual card container", cardId);
                const data = await getCardById(cardId);
                console.log('Card details:', data);
                setCardDetails(data);
            } catch (error) {
                console.error('Error fetching card details:', error);
            } finally {
                setLoading(false);
            }
        };

        if (cardId) {
            fetchCardDetails();
        }
    }, [cardId]);





    const {
        setVirtualBackgroundContextCardName,
        setVirtualBackgroundContextCompany,
        setVirtualBackgroundContextJobTitle,
        setVirtualBackgroundContextLocation,
        setVirtualBackgroundContextQRCodeColor,
        setVirtualBackgroundContextShowQRCode,
        setVirtualBackgroundContextCondensedView,
        setVirtualBackgroundContextQRCodeLogoUrl,
        setVirtualBackgroundContextBackgroundImageUrl,
        setVirtualBackgroundContextShowCardName,
        setVirtualBackgroundContextShowCompany,
        setVirtualBackgroundContextShowJobTitle,
        setVirtualBackgroundContextShowLocation,
        backgroundCardState,

    } = useVirtualBackgroundContext();

    const {
        virtualBackgroundContextCondensedView,
        virtualBackgroundContextShowCardName,
        virtualBackgroundContextShowCompany,
        virtualBackgroundContextShowJobTitle,
        virtualBackgroundContextShowLocation,
        virtualBackgroundContextShowQRCode,
        virtualBackgroundContextCardName,
        virtualBackgroundContextCompany,
        virtualBackgroundContextJobTitle,
        virtualBackgroundContextLocation,
        virtualBackgroundContextBackgroundImageUrl // Destructure showQRCode state
    } = backgroundCardState;




    useEffect(() => {
        if (cardDetails?.card) { // Ensure cardDetails.card exists
            console.log('Populating context with card details:', cardDetails); // Log when updating context
            const cardName = cardDetails.card.contact_info?.fullName || '';
            const jobTitle = cardDetails.card.contact_info?.jobTitle || '';
            const company = cardDetails.card.contact_info?.company || '';
            const location = cardDetails.card.contact_info?.location || '';

            if (backgroundCardState[cardId]?.virtualBackgroundContextCardName !== cardName) {
                setVirtualBackgroundContextCardName(cardId, cardName);
            }

            if (backgroundCardState[cardId]?.virtualBackgroundContextJobTitle !== jobTitle) {
                setVirtualBackgroundContextJobTitle(cardId, jobTitle);
            }

            if (backgroundCardState[cardId]?.virtualBackgroundContextCompany !== company) {
                setVirtualBackgroundContextCompany(cardId, company);
            }

            if (backgroundCardState[cardId]?.virtualBackgroundContextLocation !== location) {
                setVirtualBackgroundContextLocation(cardId, location);
            }
        }
    }, [cardDetails,
        cardId,
        setVirtualBackgroundContextCardName,
        setVirtualBackgroundContextJobTitle,
        setVirtualBackgroundContextCompany,
        setVirtualBackgroundContextLocation,
        backgroundCardState]);




    console.log('Current card name from demo2:', backgroundCardState[cardId]?.virtualBackgroundContextCardName);
    console.log('Current job title from demo2:', backgroundCardState[cardId]?.virtualBackgroundContextJobTitle);
    console.log('Current company from demo2:', backgroundCardState[cardId]?.virtualBackgroundContextCompany);
    console.log('Current location from demo2:', backgroundCardState[cardId]?.virtualBackgroundContextLocation);




    // Add this new useEffect after your existing fetch images useEffect
    // Add this useEffect after your image fetch useEffect
    useEffect(() => {
        if (images.length > 0 && cardId && backgroundCardState && !backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl) {
            console.log('Setting initial background image:', images[0].webformatURL);
            setVirtualBackgroundContextBackgroundImageUrl(cardId, images[0].webformatURL);
        }
    }, [images, cardId, backgroundCardState, setVirtualBackgroundContextBackgroundImageUrl]);

    const handleImageClick = (url, asset = null) => {
        // If it's a virtual background asset with saved settings, restore them
        if (asset?.type === TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND) {
            const meta = asset.metadata || {};
            
            // CRITICAL: Check if originalBackgroundImageUrl is valid (not a broken blob URL from a past session)
            const isOriginalBgValid = meta.originalBackgroundImageUrl && !meta.originalBackgroundImageUrl.startsWith('blob:');
            
            // Use originalBackgroundImageUrl if valid, otherwise fallback to the main asset URL
            const backgroundUrl = isOriginalBgValid ? meta.originalBackgroundImageUrl : url;
            
            setVirtualBackgroundContextBackgroundImageUrl(cardId, backgroundUrl);

            // If we are using the main asset URL as a fallback, it likely has baked-in overlays.
            // In this case, we should disable dynamic overlays to avoid duplication.
            if (!isOriginalBgValid) {
                console.warn('[TeamVirtualBackgroundContainer] ⚠️ originalBackgroundImageUrl is missing or invalid. Falling back to asset URL and disabling overlays.');
                setVirtualBackgroundContextShowQRCode(cardId, false);
                setVirtualBackgroundContextShowCardName(cardId, false);
                setVirtualBackgroundContextShowCompany(cardId, false);
                setVirtualBackgroundContextShowJobTitle(cardId, false);
                setVirtualBackgroundContextShowLocation(cardId, false);
            } else {
                // Apply saved settings from metadata
                setVirtualBackgroundContextShowQRCode(cardId, meta.showQRCode ?? true);
                setVirtualBackgroundContextShowCardName(cardId, meta.showCardName ?? true);
                setVirtualBackgroundContextShowCompany(cardId, meta.showCompany ?? false);
                setVirtualBackgroundContextShowJobTitle(cardId, meta.showJobTitle ?? false);
                setVirtualBackgroundContextShowLocation(cardId, meta.showLocation ?? false);

                if (meta.qrCodeColor) {
                    setVirtualBackgroundContextQRCodeColor(cardId, meta.qrCodeColor);
                    setQrCodeColor(meta.qrCodeColor);
                }
                if (meta.condensedView !== undefined) {
                    setVirtualBackgroundContextCondensedView(cardId, meta.condensedView);
                }
                if (meta.qrLogoUrl) {
                    setVirtualBackgroundContextQRCodeLogoUrl(cardId, meta.qrLogoUrl);
                }
            }
        } else {
            // It's a regular image asset (logo, cover photo, etc.) or just a URL
            setVirtualBackgroundContextBackgroundImageUrl(cardId, url);

            // If it's a virtual background asset WITHOUT metadata (older ones), 
            // disable overlays by default to avoid duplication on the screenshot image.
            if (asset?.type === TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND) {
                setVirtualBackgroundContextShowQRCode(cardId, false);
                setVirtualBackgroundContextShowCardName(cardId, false);
                setVirtualBackgroundContextShowCompany(cardId, false);
                setVirtualBackgroundContextShowJobTitle(cardId, false);
                setVirtualBackgroundContextShowLocation(cardId, false);
            }
        }
    };





    useEffect(() => {
        const imageUrl = backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl;

        if (imageUrl) {
            console.log("Image URL set to context from useEffect:", imageUrl); // Log when the URL is set

            // Create a new Image object to load the image and get its size
            const img = new Image();
            img.src = imageUrl;

            img.onload = () => {
                console.log(`Image dimensions: ${img.width} x ${img.height}`); // Log the dimensions when the image loads
            };

            img.onerror = (error) => {
                console.error("Error loading image:", error); // Log an error if the image fails to load
            };
        }
    }, [backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl]);



    useEffect(() => {
        setVirtualBackgroundContextCondensedView(cardId, backgroundCardState[cardId]?.virtualBackgroundContextCondensedView ?? false); // Update context when condensedView changes
    }, [backgroundCardState[cardId]?.virtualBackgroundContextCondensedView]);

    // Handle outside clicks to close the color picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cardPickerRef.current && !cardPickerRef.current.contains(event.target)) {
                setShowQRCodeColorPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);




    const handleQRCodeColorPickertoggle = () => {
        setShowQRCodeColorPicker((prev) => !prev);
    };

    const handleQRCodeColorChangeComplete = (color) => {
        setQrCodeColor(color.hex);
        setVirtualBackgroundContextQRCodeColor(cardId, color.hex);
    };

    // Update QR code color in the context
    useEffect(() => {
        // Only update if the new color is different from the current context color
        const currentColor = backgroundCardState[cardId]?.virtualBackgroundContextQRCodeColor;

        if (currentColor !== qrCodeColor) {
            setVirtualBackgroundContextQRCodeColor(cardId, qrCodeColor);
        }
    }, [qrCodeColor, backgroundCardState, cardId]); // Only watch qrCodeColor and necessary context state


    // Fetch virtual background images
    useEffect(() => {
        const fetchImages = async () => {
            try {
                const fetchedImages = await fetchVirtualBackgroundImages(); // Example function to fetch images
                setImages(fetchedImages);
            } catch (error) {
                console.error('Error fetching virtual background images:', error);
            }
        };

        fetchImages();
    }, []); // Only fetch images once when the component mounts

    // Pagination logic
    const isExternalPagination = typeof externalPage === 'number' && typeof onExternalPageChange === 'function';
    const activePage = isExternalPagination ? externalPage : currentPage;
    const setActivePage = isExternalPagination ? onExternalPageChange : setCurrentPage;

    const indexOfLastImage = activePage * imagesPerPage;
    const indexOfFirstImage = indexOfLastImage - imagesPerPage;
    const currentImages = images.slice(indexOfFirstImage, indexOfLastImage);

    const nextPage = () => {
        if (activePage < Math.ceil(images.length / imagesPerPage)) {
            setActivePage((prev) => prev + 1);
        }
    };

    const prevPage = () => {
        if (activePage > 1) {
            setActivePage((prev) => prev - 1);
        }
    };

    useEffect(() => {
        if (onPaginationInfo) {
            const totalPages = Math.ceil(images.length / imagesPerPage) || 1;
            onPaginationInfo({
                currentPage: activePage,
                totalPages,
                canPrev: activePage > 1,
                canNext: activePage < totalPages
            });
        }
    }, [activePage, images.length, imagesPerPage, onPaginationInfo]);




    return (
        <Container maxWidth="sm">
            <Box my={4}>
                {!hideHeading && (
                    <Box sx={{ marginBottom: '10px' }}>
                        <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f' }} gutterBottom align="left">
                            Virtual background
                        </Typography>
                        <DarkButtonWithIcon
                            text="Use Team Assets"
                            icon={<PhotoLibraryOutlinedIcon />}
                            iconPosition="start"
                            onClick={(event) => setAssetPickerAnchorEl(event.currentTarget)}
                            sx={{ marginTop: '0.75rem' }}
                        />
                    </Box>
                )}
                <TeamAssetPickerPopover
                    anchorEl={assetPickerAnchorEl}
                    onClose={() => setAssetPickerAnchorEl(null)}
                    assets={availableTeamAssets}
                    onSelectAsset={(asset) => {
                        if (asset?.url) {
                            handleImageClick(asset.url, asset);
                            setAssetPickerAnchorEl(null);
                        }
                    }}
                    onEmptyActionClick={() => navigate('/team/team-assets')}
                    selectedAssetUrl={backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl || ''}
                    showUploadButton={false}
                    showDivider={false}
                    showCloseButton={false}
                    previewWidth="180px"
                    previewHeight="70px"
                    previewRadius="8px"
                    paperSx={{ width: '420px', maxWidth: '80vw', padding: '1.1rem 1.2rem 1.2rem' }}
                />
                {loading ? (
                    <CircularProgress />
                ) : (
                    <Box sx={{ marginTop: 5 }}>
                        {/* QR Code Color Picker */}
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '13px' }}>
                            Choose QR color
                        </Typography>
                        <Box
                            sx={{
                                marginBottom: 2,
                                width: qrColorRowWidth,
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <IconButton
                                    onClick={handleQRCodeColorPickertoggle}
                                    sx={{
                                        position: 'relative',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            border: `2px solid ${qrCodeColor}`,
                                        }}
                                    />
                                    <ColorizeOutlinedIcon sx={{ color: '#828282', fontSize: 16 }} />
                                </IconButton>

                                {colorOptions.map((color, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => handleQRCodeColorChangeComplete({ hex: color })}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            position: 'relative',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                border: `2px solid ${qrCodeColor === color ? '#dbdbdb' : '#ffffff'}`,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                width: '85%',
                                                height: '85%',
                                                borderRadius: '50%',
                                                backgroundColor: color,
                                            }}
                                        />
                                    </Box>
                                ))}

                                {showQRCodeColorPicker && (
                                    <Box sx={{ position: 'absolute', top: '40px', right: 0, zIndex: 2 }} ref={cardPickerRef}>
                                        <ChromePicker color={qrCodeColor} onChange={handleQRCodeColorChangeComplete} disableAlpha />
                                    </Box>
                                )}
                            </Box>
                        </Box>


                        {/* QR Code Logo Component*/}
                        <Box sx={{ marginTop: 4 }}>
                            <TeamVirtualBackgroundCustomLogo
                                key={`logo-${refreshKey}`}
                                forceReset={refreshKey}
                                contextId={cardId}
                            />
                        </Box>


                        {/* Checkboxes Container */}
                        <Box sx={{ marginTop: 4 }}>
                            <FormControl component="fieldset" variant="standard">
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={backgroundCardState[cardId]?.virtualBackgroundContextCondensedView}
                                                    onChange={(event) => setVirtualBackgroundContextCondensedView(cardId, event.target.checked)} // Directly update with the new value
                                                    color="dark"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Condensed View
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#828282' }}>
                                                        Optimal for Microsoft Teams
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={backgroundCardState[cardId]?.virtualBackgroundContextShowQRCode}
                                                    onChange={(event) => setVirtualBackgroundContextShowQRCode(cardId, event.target.checked)}
                                                    color="dark"
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>

                                                        QR Code                          </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#828282' }}>
                                                        QR Code can be customized in the QR Code section
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>

                                    {/* Name and Company Checkboxes in Row */}
                                    <Grid container item spacing={1}>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={backgroundCardState[cardId]?.virtualBackgroundContextShowCardName}
                                                        onChange={(event) => setVirtualBackgroundContextShowCardName(cardId, event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Name
                                                    </Typography>
                                                }

                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={backgroundCardState[cardId]?.virtualBackgroundContextShowCompany}
                                                        onChange={(event) => setVirtualBackgroundContextShowCompany(cardId, event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Company
                                                    </Typography>
                                                }
                                            />
                                        </Grid>

                                        {/* Job Title and Location Checkboxes in Row */}
                                        <Grid item xs={6}>
                                            <FormControlLabel

                                                control={
                                                    <Checkbox
                                                        checked={backgroundCardState[cardId]?.virtualBackgroundContextShowJobTitle}
                                                        onChange={(event) => setVirtualBackgroundContextShowJobTitle(cardId, event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Job Title
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={backgroundCardState[cardId]?.virtualBackgroundContextShowLocation}
                                                        onChange={(event) => setVirtualBackgroundContextShowLocation(cardId, event.target.checked)}
                                                        color="dark"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                                                        Location
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Box>

                        {/* New Container with Background Color */}


                        <Box sx={{ backgroundColor: '#f7f7f7', padding: '8px', borderRadius: '8px', marginTop: 4 }}>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        setSelectedButton('library');
                                    }}
                                    sx={{
                                        flexGrow: 1,
                                        borderRadius: '8px',
                                        backgroundColor: selectedButton === 'library' ? 'white' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        '&:active': {
                                            backgroundColor: 'white', // Keeps the background white when clicked
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                            backgroundColor: 'white', // Keeps the background white when focused
                                        },
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: "#4f4f4f",
                                        textTransform: 'none', // Prevents text from being in uppercase
                                    }}
                                >
                                    Choose from Library
                                </Button>

                                {showTeamAssetsTab && (
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            setSelectedButton('team-assets');
                                        }}
                                        sx={{
                                            flexGrow: 1,
                                            borderRadius: '8px',
                                            backgroundColor: selectedButton === 'team-assets' ? 'white' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'white',
                                            },
                                            '&:active': {
                                                backgroundColor: 'white',
                                            },
                                            '&:focus': {
                                                outline: 'none',
                                                backgroundColor: 'white',
                                            },
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: "#4f4f4f",
                                            textTransform: 'none',
                                        }}
                                    >
                                        Team Assets
                                    </Button>
                                )}

                                <Button
                                    variant="text"
                                    onClick={() => {

                                        setSelectedButton('upload');
                                    }}
                                    sx={{
                                        flexGrow: 1,
                                        borderRadius: '8px',
                                        backgroundColor: selectedButton === 'upload' ? 'white' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        '&:active': {
                                            backgroundColor: 'white', // Keeps the background white when clicked
                                        },
                                        '&:focus': {
                                            outline: 'none',
                                            backgroundColor: 'white', // Keeps the background white when focused
                                        },
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: "#4f4f4f",
                                        textTransform: 'none', // Prevents text from being in uppercase
                                    }}
                                >
                                    Upload Image
                                </Button>
                            </Box>








                        </Box>
                        {/* ... */}

                        {selectedButton === 'library' && (
                            <>
                                {/* Image Grid Display */}
                                <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ marginTop: "2px" }}>
                                    {currentImages.map((image, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={4} key={index}> {/* Responsive grid */}
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: 0,
                                                    paddingBottom: '75%', // 4:3 aspect ratio
                                                    position: 'relative',
                                                    borderRadius: 3, // Increased border radius for more rounded corners
                                                    border: backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl === image.webformatURL ? '4px solid #2fabed' : 'none',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleImageClick(image.webformatURL)}
                                            >
                                                <img
                                                    src={image.webformatURL}
                                                    alt={image.tags}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: 8, // Increased border radius for more rounded corners
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>

                                {!hidePagination && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 5 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={prevPage}
                                            disabled={activePage === 1}
                                            startIcon={<NorthIcon sx={{ transform: 'rotate(-90deg)' }} />}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                color: '#4f4f4f',
                                                textTransform: 'none',
                                                padding: '6px 28px',
                                                borderRadius: '18px',
                                                borderColor: activePage === 1 ? '#e0e0e0' : '#dfdfdf',
                                            }}
                                        >
                                            Prev
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={nextPage}
                                            disabled={activePage >= Math.ceil(images.length / imagesPerPage)}
                                            endIcon={<NorthIcon sx={{ transform: 'rotate(90deg)' }} />}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                color: '#4f4f4f',
                                                borderRadius: '18px',
                                                padding: '6px 28px',

                                                textTransform: 'none',
                                                borderColor: activePage >= Math.ceil(images.length / imagesPerPage) ? '#e0e0e0' : '#dfdfdf',
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}


                            </>
                        )}

                        {/* ... */}


                        {selectedButton === 'team-assets' && (
                            <>
                                {availableTeamAssets.length === 0 ? (
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#828282', marginTop: 2 }}>
                                        No team assets set
                                    </Typography>
                                ) : (
                                    <Grid container spacing={2} alignItems="center" justifyContent="center" sx={{ marginTop: "2px" }}>
                                        {availableTeamAssets.map((asset, index) => (
                                            <Grid item xs={12} sm={6} md={4} lg={4} key={asset._id || index}>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: 0,
                                                        paddingBottom: '75%',
                                                        position: 'relative',
                                                        borderRadius: 3,
                                                        border: backgroundCardState[cardId]?.virtualBackgroundContextBackgroundImageUrl === asset.url ? '4px solid #2fabed' : 'none',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => handleImageClick(asset.url, asset)}
                                                >
                                                    <img
                                                        src={asset.url}
                                                        alt={asset.name || 'Team asset'}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            borderRadius: 8,
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </>
                        )}

                        {selectedButton === 'upload' && (
                            <TeamUploadImage onImageSelect={(croppedImageUrl) => {
                                setVirtualBackgroundContextBackgroundImageUrl(cardId, croppedImageUrl);
                            }} />
                        )}
                    </Box>




                )}
            </Box>
        </Container>
    );
};

export default TeamVirtualBackgroundContainer;

